// src/components/HomeView.js

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import socket from '../socket'; // Dein Client-Socket
import Modal from 'react-modal';
import './css/HomeView.css';
import { fetchJson } from '../utils/api';
import { getTeamIdFromPlayer } from '../utils/player';
import { isValidHttpUrl } from '../utils/url';
import Button from './ui/Button';
import NoteEditor from './NoteEditor';
import {
    fromServerQuestion,
    loadQuestions,
    subscribeQuizState,
    updateQuestion,
} from '../integrations/quizApiAdapter';
import { normalizeAttachments } from '../utils/attachments';
import useAttachmentControls from '../hooks/useAttachmentControls';
import AttachmentRegiePanel from './AttachmentRegiePanel';
import { useSocketDebugState } from '../utils/socketDebug';
import useSocketEvent from '../utils/useSocketEvent';
import safeStorage from '../utils/safeStorage';
import { formatDeltaMs } from '../utils/formatDeltaMs';

const customStyles = {
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
    },
};

const COUNTDOWN_STORAGE_KEY = 'moderatorCountdownDefaultSeconds';
const TEAM_TEXT_SYNC_DELAY_MS = 220;

Modal.setAppElement('#root');

function HomeView() {
    // ==============================
    // 1) REACT STATES
    // ==============================
    const [quizActive, setQuizActive] = useState(false);
    const [paused, setPaused] = useState(false);
    const [startingQuiz, setStartingQuiz] = useState(false);
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [currentAnswers, setCurrentAnswers] = useState([]);
    const [countdown, setCountdown] = useState(null);
    const [, setTimer] = useState(0);

    const [predictionAnswers, setPredictionAnswers] = useState({});
    const [, setPredictionActive] = useState(false);

    const [selectedAnswers, setSelectedAnswers] = useState({});
    const [feedback, setFeedback] = useState(null);

    const [teams, setTeams] = useState([]);
    const [, setUpdatingTeamIds] = useState(new Set());
    const [, setAllPlayers] = useState([]);
    const [noTeamPlayers, setNoTeamPlayers] = useState([]);

    const [allQuestions, setAllQuestions] = useState([]);
    const [filter, setFilter] = useState({ kategorie: '', schwierigkeit: '', playsetId: '' });
    const [filteredQuestions, setFilteredQuestions] = useState([]);
    const [selectedQuestionId, setSelectedQuestionId] = useState(null);
    const [questionCache, setQuestionCache] = useState({});

    const [isStopPauseModalOpen, setIsStopPauseModalOpen] = useState(false);
    const [isEndActionModalOpen, setIsEndActionModalOpen] = useState(false);
    const { getState: getAttachmentState, actions: attachmentActions } = useAttachmentControls();
    const socketDebug = useSocketDebugState();
    const [selectedAttachmentKey, setSelectedAttachmentKey] = useState(null);

    // Moderator-spezifisch
    const [isModerator] = useState(true);
    const [activeTeamId, setActiveTeamId] = useState(null);

    // Iframe-Errors
    const [, setIframeErrors] = useState({});

    // Button „Richtige Antwort“ Hover
    const [hoveringCorrect, setHoveringCorrect] = useState(false);
    const [correctAnswerIndex, setCorrectAnswerIndex] = useState(null);
    const [showCorrectOutline, setShowCorrectOutline] = useState(false);

    const [savedQuestion, setSavedQuestion] = useState(null);
    const [showMatrix, setShowMatrix] = useState(false);
    const [questionsError, setQuestionsError] = useState('');
    const [, setLoadingQuestions] = useState(false);
    const [besserwisserState, setBesserwisserState] = useState(null);
    const [quizRuntimeState, setQuizRuntimeState] = useState(null);
    const [jokerReusePrompt, setJokerReusePrompt] = useState(false);
    const [jokerReuseTeamId, setJokerReuseTeamId] = useState(null);
    const [teamTextDraftByTeamId, setTeamTextDraftByTeamId] = useState({});
    const teamTextSaveTimeoutsRef = useRef({});
    const lastIncomingTeamTextRef = useRef({});

    const fetchQuestions = useCallback(async () => {
        setLoadingQuestions(true);
        setQuestionsError('');
        try {
            const data = await loadQuestions();
            setAllQuestions(Array.isArray(data) ? data : []);
            setQuestionCache((data || []).reduce((acc, q) => ({ ...acc, [q.id]: q }), {}));
        } catch (error) {
            setQuestionsError(error?.message || 'Fehler beim Laden der Fragen');
            setAllQuestions([]);
            setQuestionCache({});
        } finally {
            setLoadingQuestions(false);
        }
    }, []);

    const applyIncomingQuestion = useCallback((incoming, options = {}) => {
        if (!incoming || !incoming.id) return null;
        const mapped = fromServerQuestion(incoming);
        let mergedQuestion = mapped;

        setQuestionCache((prevCache) => {
            const existing = prevCache[mapped.id];
            mergedQuestion = existing
                ? {
                    ...existing,
                    ...mapped,
                    attachments: (mapped.attachments && mapped.attachments.length > 0) ? mapped.attachments : (existing.attachments || []),
                    note: mapped.note ?? existing.note ?? '',
                    playsets: (mapped.playsets && mapped.playsets.length > 0) ? mapped.playsets : (existing.playsets || []),
                    playsetIds: (mapped.playsetIds && mapped.playsetIds.length > 0) ? mapped.playsetIds : (existing.playsetIds || []),
                    lastPlayedAt: mapped.lastPlayedAt ?? existing.lastPlayedAt ?? null,
                    gespielt: mapped.gespielt ?? existing.gespielt,
                }
                : mapped;
            return { ...prevCache, [mapped.id]: mergedQuestion };
        });

        setAllQuestions((prev) => {
            const exists = prev.some((q) => q.id === mergedQuestion.id);
            if (exists) {
                return prev.map((q) => (q.id === mergedQuestion.id ? mergedQuestion : q));
            }
            return [...prev, mergedQuestion];
        });

        setCurrentQuestion(mergedQuestion);
        setCurrentAnswers(mergedQuestion?.antworten ?? []);

        if (options.resetState) {
            setSelectedAnswers({});
            setFeedback(null);
            setCorrectAnswerIndex(null);
        }

        return mergedQuestion;
    }, []);

    // ==============================
    // 2) DATEN LADEN (Mount)
    // ==============================
    useEffect(() => {
        socket.emit('getQuizState');

        fetchJson('/api/teams')
            .then((data) => setTeams(Array.isArray(data) ? data : []))
            .catch(() => setTeams([]));

        fetchQuestions();

        fetchJson('/api/players')
            .then((playersData) => {
                setAllPlayers(playersData);
                setNoTeamPlayers(playersData.filter((p) => !getTeamIdFromPlayer(p)));
            })
            .catch(() => {
                setAllPlayers([]);
                setNoTeamPlayers([]);
            });
    }, [fetchQuestions]);

    useEffect(() => {
        const unsubscribe = subscribeQuizState(socket, {
            onQuizState: (srvState) => {
                if (!srvState) return;
                setQuizRuntimeState(srvState);
                if (typeof srvState.matrixVisible === 'boolean') {
                    setShowMatrix(srvState.matrixVisible);
                }
                setBesserwisserState(srvState.besserwisser ?? null);
                setQuizActive(srvState.quizActive);
                setPaused(srvState.paused);
                setActiveTeamId(srvState.activeTeamId || null);
                setSelectedAnswers(srvState.selectedAnswers || {});
                setCorrectAnswerIndex(srvState.highlightedAnswerIndex ?? null);
                setPredictionActive(srvState.predictionActive);
                setPredictionAnswers(srvState.predictionAnswers || {});
                if (srvState.currentQuestion) {
                    const merged = applyIncomingQuestion(srvState.currentQuestion);
                    if (showMatrix) {
                        merged && setSavedQuestion(merged);
                        setCurrentQuestion(null);
                    } else {
                        setSavedQuestion(null);
                    }
                }
            },
            onQuestionSelected: (payload) => {
                const merged = applyIncomingQuestion(payload, { resetState: true });
                setCountdown(null);
                setTimer(0);
                setPredictionAnswers({});
                setPredictionActive(false);

                if (showMatrix) {
                    merged && setSavedQuestion(merged);
                    setCurrentQuestion(null);
                } else {
                    setSavedQuestion(null);
                }
                return merged;
            },
        });

        return () => {
            unsubscribe && unsubscribe();
        };
    }, [applyIncomingQuestion, showMatrix]);

    // ==============================
    // 3) SOCKET-LISTENER
    // ==============================
    const handleQuizStarted = useCallback(({ message }) => {
        setQuizActive(true);
        setPaused(false);
        setStartingQuiz(false);
        console.log('[quizStarted]', message);
    }, []);

    const handleQuizEnded = useCallback(({ message }) => {
        console.log('[quizEnded]', message);
        setQuizActive(false);
        setPaused(false);
        setCurrentQuestion(null);
        setCurrentAnswers([]);
        setCountdown(null);
        setTimer(0);
        setSelectedAnswers({});
        setFeedback(null);
        setActiveTeamId(null);
        setPredictionAnswers({});
        alert(message);
    }, []);

    const handleQuizPaused = useCallback(({ message }) => {
        console.log('[quizPaused]', message);
        setPaused(true);
        alert(message);
    }, []);

    const handleQuizResumed = useCallback(({ message }) => {
        console.log('[quizResumed]', message);
        setPaused(false);
        alert(message);
    }, []);

    const handleNoMoreQuestions = useCallback(({ message }) => {
        alert(message);
    }, []);

    const handlePredictionUpdated = useCallback(({ teamId, willBeCorrect }) => {
        setPredictionAnswers((prev) => ({
            ...prev,
            [teamId]: willBeCorrect,
        }));
    }, []);

    const handlePredictionStarted = useCallback(({ timeLeft }) => {
        console.log('[HomeView] predictionStarted:', timeLeft);
        setPredictionAnswers({});
    }, []);

    const handlePredictionTick = useCallback(() => {
    }, []);

    const handlePredictionEnded = useCallback(() => {
        console.log('[HomeView] predictionEnded');
    }, []);

    const handlePredictionsRevealed = useCallback(({ predictionAnswers }) => {
        console.log('[HomeView] predictionsRevealed =>', predictionAnswers);
        setPredictionAnswers(predictionAnswers || {});
    }, []);

    const handleCountdownStarted = useCallback(({ timeLeft }) => {
        console.log('[countdownStarted]', timeLeft);
        setCountdown(timeLeft);
        setTimer(timeLeft);
    }, []);

    const handleCountdownTick = useCallback(({ timeLeft }) => {
        setCountdown(timeLeft);
        setTimer(timeLeft);
    }, []);

    const handleCountdownEnded = useCallback(() => {
        console.log('[countdownEnded]');
    }, []);

    const handleCountdownPaused = useCallback(({ timeLeft }) => {
        console.log('[countdownPaused]', timeLeft);
        setCountdown(timeLeft);
        setTimer(timeLeft);
    }, []);

    const handleTeamsUpdated = useCallback((updatedTeam) => {
        console.log('[teamsUpdated]', updatedTeam);
        const { id, spieler } = updatedTeam;
        setTeams((prevTeams) =>
            prevTeams.map((t) =>
                t.id === id ? { ...t, ...updatedTeam } : t
            )
        );
        if (spieler) {
            setAllPlayers((prev) =>
                prev.map((p) => {
                    const found = spieler.find((s) => s.id === p.id);
                    return found ? { ...p, team: found.team } : p;
                })
            );
        }
    }, []);

    const handleTeamCreated = useCallback((newTeam) => {
        console.log('[teamCreated]', newTeam);
        setTeams((prev) => [...prev, newTeam]);
    }, []);

    const handleTeamDeleted = useCallback(({ teamId }) => {
        console.log('[teamDeleted]', teamId);
        setTeams((prev) => prev.filter((t) => t.id !== teamId));
        setAllPlayers((prevPlayers) => {
            const updatedPlayers = prevPlayers.map((player) =>
                getTeamIdFromPlayer(player) === teamId
                    ? { ...player, team: null, teamId: null }
                    : player
            );
            setNoTeamPlayers(
                updatedPlayers.filter((player) => !getTeamIdFromPlayer(player))
            );
            return updatedPlayers;
        });
    }, []);

    const handlePlayerAdded = useCallback((newPlayer) => {
        console.log('[playerAdded]', newPlayer);
        setAllPlayers((prev) => {
            const exists = prev.some((player) => player.id === newPlayer.id);
            if (exists) {
                return prev.map((player) =>
                    player.id === newPlayer.id ? newPlayer : player
                );
            }
            return [...prev, newPlayer];
        });

        const playerTeamId = getTeamIdFromPlayer(newPlayer);
        if (playerTeamId != null) {
            setTeams((prev) =>
                prev.map((team) => {
                    if (team.id === playerTeamId) {
                        const alreadyIn = team.spieler.some(
                            (sp) => sp.id === newPlayer.id
                        );
                        const updatedRoster = alreadyIn
                            ? team.spieler.map((sp) =>
                                  sp.id === newPlayer.id ? newPlayer : sp
                              )
                            : [...team.spieler, newPlayer];
                        return { ...team, spieler: updatedRoster };
                    }
                    return {
                        ...team,
                        spieler: team.spieler.filter(
                            (sp) => sp.id !== newPlayer.id
                        ),
                    };
                })
            );
            setNoTeamPlayers((prev) =>
                prev.filter((player) => player.id !== newPlayer.id)
            );
        } else {
            setTeams((prev) =>
                prev.map((team) => ({
                    ...team,
                    spieler: team.spieler.filter(
                        (sp) => sp.id !== newPlayer.id
                    ),
                }))
            );
            setNoTeamPlayers((prev) => {
                const withoutPlayer = prev.filter(
                    (player) => player.id !== newPlayer.id
                );
                return [...withoutPlayer, { ...newPlayer, team: null }];
            });
        }
    }, []);

    const handlePlayerUpdated = useCallback((updatedPlayer) => {
        console.log('[playerUpdated]', updatedPlayer);
        setAllPlayers((prevPlayers) =>
            prevPlayers.map((p) => (p.id === updatedPlayer.id ? updatedPlayer : p))
        );

        const updatedTeamId = getTeamIdFromPlayer(updatedPlayer);
        setTeams((prev) =>
            prev.map((team) => {
                const containsPlayer = team.spieler.some(
                    (sp) => sp.id === updatedPlayer.id
                );
                if (team.id === updatedTeamId) {
                    const updatedRoster = containsPlayer
                        ? team.spieler.map((sp) =>
                              sp.id === updatedPlayer.id ? updatedPlayer : sp
                          )
                        : [...team.spieler, updatedPlayer];
                    return { ...team, spieler: updatedRoster };
                }

                if (containsPlayer) {
                    return {
                        ...team,
                        spieler: team.spieler.filter(
                            (sp) => sp.id !== updatedPlayer.id
                        ),
                    };
                }

                return team;
            })
        );

        if (updatedTeamId != null) {
            setNoTeamPlayers((prev) =>
                prev.filter((pl) => pl.id !== updatedPlayer.id)
            );
        } else {
            setNoTeamPlayers((prev) => {
                const withoutPlayer = prev.filter(
                    (pl) => pl.id !== updatedPlayer.id
                );
                return [...withoutPlayer, { ...updatedPlayer, team: null }];
            });
        }
    }, []);

    const handlePlayerDeleted = useCallback(({ playerId }) => {
        console.log('[playerDeleted]', playerId);
        setAllPlayers((prev) => prev.filter((p) => p.id !== playerId));
        setTeams((prev) =>
            prev.map((t) => ({
                ...t,
                spieler: t.spieler.filter((pl) => pl.id !== playerId),
            }))
        );
        setNoTeamPlayers((prev) => prev.filter((p) => p.id !== playerId));
    }, []);

    const handleAnswerSelected = useCallback(({ teamId, answerIndex }) => {
        console.log('[HomeView] answerSelected =>', teamId, 'Index:', answerIndex);
        setSelectedAnswers((prev) => ({
            ...prev,
            [teamId]: answerIndex,
        }));
    }, []);

    const handleHighlightCorrectAnswer = useCallback(({ correctAnswerIndex }) => {
        console.log('[HomeView] highlightCorrectAnswer =>', correctAnswerIndex);
        setCorrectAnswerIndex(correctAnswerIndex);
    }, []);

    const handleErrorMessage = useCallback((data = {}) => {
        const { message } = data;
        console.error('[ShowPage] errorMessage:', message);
        alert(message || 'Unbekannter Fehler');
    }, []);

    const handleConnect = useCallback(() => {
        console.log('[HomeView] connected => getQuizState');
        socket.emit('getQuizState');
    }, []);

    const handleCorrectAnswer = useCallback(({ correctIndex }) => {
        console.log('[HomeView] correctAnswer =>', correctIndex);
    }, []);

    useSocketEvent(socket, 'quizStarted', handleQuizStarted);
    useSocketEvent(socket, 'quizEnded', handleQuizEnded);
    useSocketEvent(socket, 'quizPaused', handleQuizPaused);
    useSocketEvent(socket, 'quizResumed', handleQuizResumed);
    useSocketEvent(socket, 'noMoreQuestions', handleNoMoreQuestions);
    useSocketEvent(socket, 'predictionUpdated', handlePredictionUpdated);
    useSocketEvent(socket, 'predictionStarted', handlePredictionStarted);
    useSocketEvent(socket, 'predictionTick', handlePredictionTick);
    useSocketEvent(socket, 'predictionEnded', handlePredictionEnded);
    useSocketEvent(socket, 'predictionsRevealed', handlePredictionsRevealed);
    useSocketEvent(socket, 'countdownStarted', handleCountdownStarted);
    useSocketEvent(socket, 'countdownTick', handleCountdownTick);
    useSocketEvent(socket, 'countdownEnded', handleCountdownEnded);
    useSocketEvent(socket, 'countdownPaused', handleCountdownPaused);
    useSocketEvent(socket, 'teamsUpdated', handleTeamsUpdated);
    useSocketEvent(socket, 'teamCreated', handleTeamCreated);
    useSocketEvent(socket, 'teamDeleted', handleTeamDeleted);
    useSocketEvent(socket, 'playerAdded', handlePlayerAdded);
    useSocketEvent(socket, 'playerUpdated', handlePlayerUpdated);
    useSocketEvent(socket, 'playerDeleted', handlePlayerDeleted);
    useSocketEvent(socket, 'answerSelected', handleAnswerSelected);
    useSocketEvent(socket, 'highlightCorrectAnswer', handleHighlightCorrectAnswer);
    useSocketEvent(socket, 'errorMessage', handleErrorMessage);
    useSocketEvent(socket, 'connect', handleConnect);
    useSocketEvent(socket, 'correctAnswer', handleCorrectAnswer);

    // ==============================
    // 5) FRAGEN-FILTER
    // ==============================
    const categories = useMemo(() => {
        const entries = allQuestions
            .map((q) => q.kategorie)
            .filter(Boolean);
        return Array.from(new Set(entries)).sort((a, b) => a.localeCompare(b));
    }, [allQuestions]);

    const difficulties = useMemo(() => {
        const levels = allQuestions
            .map((q) => q.schwierigkeitsgrad)
            .filter((lvl) => lvl !== undefined && lvl !== null);
        return Array.from(new Set(levels)).sort((a, b) => a - b);
    }, [allQuestions]);

    const playsetOptions = useMemo(() => {
        const map = new Map();
        allQuestions.forEach((q) => {
            (q.playsets || []).forEach((playset) => {
                if (playset?.id) {
                    map.set(String(playset.id), playset.name || `Playset ${playset.id}`);
                }
            });
        });
        return Array.from(map.entries())
            .map(([id, name]) => ({ id, name }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [allQuestions]);

    useEffect(() => {
        const filtered = allQuestions.filter((q) => {
            const katOk = filter.kategorie
                ? q.kategorie?.toLowerCase()?.includes(filter.kategorie.toLowerCase())
                : true;
            const lvlOk = filter.schwierigkeit
                ? getSchwierigkeitsgradString(q.schwierigkeitsgrad)
                    .toLowerCase()
                    .includes(filter.schwierigkeit.toLowerCase())
                : true;
            const playsetOk = filter.playsetId
                ? (q.playsetIds || []).map(String).includes(String(filter.playsetId))
                : true;
            return katOk && lvlOk && playsetOk;
        });
        setFilteredQuestions(filtered);
    }, [allQuestions, filter]);

    function getSchwierigkeitsgradString(level) {
        switch (level) {
            case 1:
                return 'Einfach';
            case 2:
                return 'Mittel';
            case 3:
                return 'Schwer';
            default:
                return 'Unbekannt';
        }
    }

    const currentAttachments = useMemo(
        () => normalizeAttachments(currentQuestion?.attachments || [], currentQuestion?.id),
        [currentQuestion]
    );
    const attachDebugEnabled = socketDebug.enabled;

    useEffect(() => {
        setSelectedAttachmentKey(null);
    }, [currentQuestion?.id]);

    useEffect(() => {
        setShowCorrectOutline(false);
    }, [currentQuestion?.id]);

    // ==============================
    // 6) HILFSFUNKTIONEN
    // ==============================
    function handleFilterChange(e) {
        const { name, value } = e.target;
        setFilter((prev) => ({ ...prev, [name]: value }));
    }

    function handleIframeError(playerId) {
        setIframeErrors((prev) => ({ ...prev, [playerId]: true }));
    }

    const handleAttachmentSelectKey = useCallback((urlKey) => {
        setSelectedAttachmentKey(urlKey || null);
    }, []);

    function handleToggleModeratorView() {
        socket.emit('toggleModeratorView');
    }

    function toggleMatrixDisplay() {
        if (!showMatrix) {
            // Matrix anzeigen: Verstecke aktuelle Frage (aber speichere sie)
            if (currentQuestion) setSavedQuestion(currentQuestion);
            setCurrentQuestion(null);
            setCountdown(null);
            setPredictionAnswers({});
            setPredictionActive(false);
            setShowMatrix(true);
            socket.emit('toggleMatrix', { visible: true });
        } else {
            // Matrix ausblenden: Stelle gespeicherte Frage wieder her (sofern vorhanden)
            setShowMatrix(false);
            socket.emit('toggleMatrix', { visible: false });
            if (savedQuestion) {
                setCurrentQuestion(savedQuestion);
                setSavedQuestion(null);
            }
        }
    }

    // ==============================
    // 7) QUIZ-KONTROLLEN
    // ==============================
    function handleStartQuiz() {
        setStartingQuiz(true);
        socket.emit('startQuiz', (resp) => {
            console.log('[HomeView] startQuiz callback =>', resp);
        });
    }

    function handleStopOrPauseQuiz() {
        setIsStopPauseModalOpen(true);
    }

    function handleConfirmStop(action) {
        if (action === 'stop') {
            setIsStopPauseModalOpen(false);
            setIsEndActionModalOpen(true);
        } else if (action === 'pause') {
            setIsStopPauseModalOpen(false);
            socket.emit('pauseQuiz', (res) => {
                if (res && res.success === false) {
                    alert(res.message);
                }
            });
        }
    }

    function handleEndAction(action) {
        setIsEndActionModalOpen(false);
        socket.emit('endQuiz', { action }, (resp) => {
            console.log('[HomeView] endQuiz => resp', resp);
            if (!resp?.success) alert(resp?.message || 'Fehler beim Beenden');
        });
    }

    function handleCancelStopPause() {
        setIsStopPauseModalOpen(false);
    }

    function handleCancelEndAction() {
        setIsEndActionModalOpen(false);
    }

    function resetBesserwisserUi() {
        setBesserwisserState(null);
        setJokerReusePrompt(false);
    }

    function handleNextQuestion() {
        resetBesserwisserUi();
        socket.emit('nextQuestion');
        setCountdown(null);
        setTimer(0);
        if (currentQuestion) setSavedQuestion(currentQuestion);
        setCurrentQuestion(null);
        setPredictionAnswers({});
        setPredictionActive(false);
    }

    function handleStartPredictionCountdown() {
        const duration = prompt('Wie lange sollen Teams tippen können (Sek)?', '10');
        const sec = parseInt(duration, 10);
        if (!isNaN(sec) && sec > 0) {
            socket.emit('startPredictionCountdown', sec, (resp) => {
                if (!resp?.success) {
                    alert('Konnte Vorhersage-Countdown nicht starten.');
                }
            });
        }
    }

    function renderPredictionForTeam(teamId) {
        // Hole den Tipp
        const tip = predictionAnswers[teamId];
        if (tip === true) {
            return <span className="prediction-marker"> JA</span>;
        } else if (tip === false) {
            return <span className="prediction-marker"> NEIN</span>;
        } else {
            // Keine Vorhersage
            return <span className="prediction-marker"> </span>;
        }
    }

    function handleRevealPredictions() {
        socket.emit('revealPredictions');
    }

    function getCountdownDefaultSeconds() {
        const stored = Number.parseInt(safeStorage.getItem(COUNTDOWN_STORAGE_KEY), 10);
        if (Number.isFinite(stored) && stored > 0) {
            return stored;
        }
        return 180;
    }

    function handleStartCountdown() {
        const defaultSeconds = getCountdownDefaultSeconds();
        const duration = prompt('Countdown-Dauer in Sekunden:', String(defaultSeconds));
        const sec = parseInt(duration, 10);
        if (!isNaN(sec) && sec > 0) {
            safeStorage.setItem(COUNTDOWN_STORAGE_KEY, String(sec));
            socket.emit('startCountdown', sec);
        }
    }

    function handlePauseCountdown() {
        socket.emit('pauseCountdown');
    }

    function handleResumeCountdown() {
        const sec = parseInt(prompt('Countdown fortsetzen ab Sek.', '30'), 10);
        if (!isNaN(sec) && sec > 0) {
            socket.emit('resumeCountdown', sec);
        }
    }

    // ==============================
    // 8) FRAGE/ANTWORT-KLICKS
    // ==============================
    function handleQuestionClick() {
        if (!currentQuestion || !currentQuestion.id) return;
        setCountdown(null);
        setTimer(0);
        socket.emit('questionClicked', {
            frage: currentQuestion.frage,
            antworten: currentQuestion.antworten,
            id: currentQuestion.id,
        });
    }

    function handleAnswerClick(antwort) {
        if (!currentQuestion) return;
        socket.emit('answerClicked', {
            frage: currentQuestion.frage,
            antwort,
        });
    }

    const handleNoteChange = (next) => {
        setCurrentQuestion((prev) => {
            if (!prev) return prev;
            const updated = { ...prev, note: next };
            setQuestionCache((cache) => ({
                ...cache,
                [prev.id]: { ...(cache[prev.id] || prev), note: next },
            }));
            return updated;
        });
    };

    const handleNoteSave = async (next) => {
        if (!currentQuestion?.id) return;
        const base = questionCache[currentQuestion.id] || currentQuestion;
        const payload = {
            ...base,
            note: next,
            attachments: base.attachments || [],
            playsetIds: base.playsetIds || [],
        };
        try {
            const updated = await updateQuestion(currentQuestion.id, payload);
            applyIncomingQuestion(updated);
        } catch (error) {
            console.error('Fehler beim Speichern der Notiz', error);
        }
    };

    // (NEU) handleHighlightCorrectAnswerClick mit Debug-Logs
    function handleHighlightCorrectAnswerClick() {
        if (!currentQuestion) {
            console.error('[ERROR] Keine aktuelle Frage gesetzt');
            return;
        }

        const correctIndex = currentQuestion.richtigeAntwort;
        console.log('[DEBUG] handleHighlightCorrectAnswer => currentQuestion:', currentQuestion);
        console.log('[DEBUG] handleHighlightCorrectAnswer => correctIndex:', correctIndex);
        setCorrectAnswerIndex(correctIndex);

        if (
            typeof correctIndex !== 'number' ||
            correctIndex < 0 ||
            correctIndex >= currentAnswers.length
        ) {
            console.error(
                '[ERROR] Keine korrekte Antwort definiert oder Index ungültig! currentAnswers.length=',
                currentAnswers.length
            );
            alert('Keine korrekte Antwort definiert oder Index ungültig!');
            return;
        }

        console.log('[DEBUG] Richtige Antwort korrekt:', correctIndex);

        // Emit nur den Index
        socket.emit('highlightCorrectAnswer', { correctIndex });
    }

    // ==============================
    // 9) FRAGE-AUSWAHL
    // ==============================
    function handleSelectQuestion(questionId) {
        socket.emit('selectQuestion', { questionId }, (resp) => {
            if (!resp?.success && resp?.played) {
                const reuse = window.confirm('Diese Frage wurde schon gespielt. Trotzdem verwenden?');
                if (reuse) {
                    socket.emit('selectQuestion', { questionId, force: true }, (forceResp) => {
                        if (!forceResp?.success) {
                            alert(forceResp?.message || 'Fehler beim erneuten Auswählen');
                        }
                    });
                }
            } else if (!resp?.success) {
                alert(resp?.message || 'Fehler beim Auswählen der Frage');
            }
        });
        setSelectedQuestionId(null);
    }

    function handleRandomQuestion() {
        const availableQuestions = filteredQuestions.filter((q) => !q.gespielt);
        if (availableQuestions.length === 0) {
            alert('Keine Fragen gefunden.');
            return;
        }
        const rIndex = Math.floor(Math.random() * availableQuestions.length);
        const q = availableQuestions[rIndex];
        handleSelectQuestion(q.id);
    }

    // ==============================
    // 10) TEAMS + PUNKTE
    // ==============================
    function updatePoints(teamId, newPoints) {
        setUpdatingTeamIds((prev) => new Set(prev).add(teamId));
        fetchJson(`/api/teams/${teamId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ punkte: newPoints }),
        })
            .then((updatedTeam) => {
                setTeams((prev) =>
                    prev.map((t) => (t.id === updatedTeam.id ? updatedTeam : t))
                );
                setUpdatingTeamIds((prev) => {
                    const newSet = new Set(prev);
                    newSet.delete(teamId);
                    return newSet;
                });
            })
            .catch((err) => {
                console.error('[updatePoints] Fehler:', err);
                setUpdatingTeamIds((prev) => {
                    const newSet = new Set(prev);
                    newSet.delete(teamId);
                    return newSet;
                });
            });
    }

    const handleUseBesserwisserJoker = useCallback((teamId) => {
        socket.emit('besserwisser:joker:use', { targetTeamId: String(teamId) }, (resp) => {
            if (resp?.ok) return;
            if (resp?.error === 'NEEDS_REUSE_CONFIRMATION') {
                setJokerReuseTeamId(String(teamId));
                setJokerReusePrompt(true);
                return;
            }
            alert(resp?.error || resp?.message || 'Fehler beim Setzen des Jokers.');
        });
    }, []);

    const handleConfirmJokerReuse = useCallback(() => {
        if (!jokerReuseTeamId) {
            setJokerReusePrompt(false);
            return;
        }
        socket.emit('besserwisser:joker:use', {
            targetTeamId: jokerReuseTeamId,
            confirmedReuse: true,
        }, (resp) => {
            if (!resp?.ok) {
                alert(resp?.error || resp?.message || 'Fehler beim Setzen des Jokers.');
            }
        });
        setJokerReusePrompt(false);
        setJokerReuseTeamId(null);
    }, [jokerReuseTeamId]);

    function handleSetActiveTeam(teamId) {
        if (activeTeamId === teamId) {
            socket.emit('setActiveTeam', { teamId: null }, (resp) => {
                if (!resp?.success) alert(resp?.message || 'Fehler');
            });
        } else {
            socket.emit('setActiveTeam', { teamId }, (resp) => {
                if (!resp?.success) alert(resp?.message || 'Fehler');
            });
        }
    }

    // ==============================
    // 11) Hover-Handhabung
    // ==============================
    function handleMouseEnterCorrect() {
        setHoveringCorrect(true);
    }
    function handleMouseLeaveCorrect() {
        setHoveringCorrect(false);
    }

    const bestBesserwisserTeamId = useMemo(() => {
        const entries = Object.values(besserwisserState?.answersByTeamId || {});
        let bestEntry = null;
        entries.forEach((entry) => {
            if (!entry || entry.isCorrect !== true || !Number.isFinite(entry.deltaMs)) return;
            if (!bestEntry || entry.deltaMs < bestEntry.deltaMs) {
                bestEntry = entry;
            }
        });
        return bestEntry?.teamId ?? null;
    }, [besserwisserState?.answersByTeamId]);

    const teamTextByTeamId = useMemo(() => {
        const candidateMaps = [
            quizRuntimeState?.teamTextByTeamId,
            quizRuntimeState?.teamNotesByTeamId,
            quizRuntimeState?.messagesByTeamId,
            quizRuntimeState?.sharedTextByTeamId,
            besserwisserState?.teamTextByTeamId,
            besserwisserState?.teamNotesByTeamId,
            besserwisserState?.messagesByTeamId,
            besserwisserState?.sharedTextByTeamId,
        ];

        const firstValidMap = candidateMaps.find((entry) => entry && typeof entry === 'object');
        if (firstValidMap) return firstValidMap;

        // TODO(server-state): Team-Text pro Team explizit im Quiz-State mitsenden.
        return null;
    }, [besserwisserState, quizRuntimeState]);


    useEffect(() => {
        setTeamTextDraftByTeamId((prev) => {
            const next = { ...prev };
            let changed = false;
            teams.forEach((team) => {
                const incomingText = String(teamTextByTeamId?.[team.id] ?? team?.sharedText ?? '');
                const previousIncomingText = lastIncomingTeamTextRef.current[team.id];
                const hasDraft = Object.prototype.hasOwnProperty.call(next, team.id);
                const locallyModified = hasDraft && previousIncomingText != null && next[team.id] !== previousIncomingText;

                if (!hasDraft || !locallyModified) {
                    if (next[team.id] !== incomingText) {
                        changed = true;
                    }
                    next[team.id] = incomingText;
                }

                lastIncomingTeamTextRef.current[team.id] = incomingText;
            });
            return changed ? next : prev;
        });
    }, [teamTextByTeamId, teams]);

    const persistTeamSharedText = useCallback((teamId, sharedText) => {
        const team = teams.find((entry) => String(entry.id) === String(teamId));
        if (!team?.id) return;
        if (String(team.sharedText ?? '') === sharedText) return;

        fetchJson(`/api/teams/${team.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                name: team.name,
                punkte: Number(team.punkte ?? 0),
                sharedText,
            }),
        })
            .then((updatedTeam) => {
                if (!updatedTeam?.id) return;
                setTeams((prev) => prev.map((entry) => (entry.id === updatedTeam.id ? { ...entry, ...updatedTeam } : entry)));
            })
            .catch((error) => {
                console.error('[HomeView] Fehler beim Speichern vom Team-Text:', error);
            });
    }, [teams]);

    const handleTeamTextChange = useCallback((teamId, value) => {
        setTeamTextDraftByTeamId((prev) => ({ ...prev, [teamId]: value }));
        if (teamTextSaveTimeoutsRef.current[teamId]) {
            clearTimeout(teamTextSaveTimeoutsRef.current[teamId]);
        }

        teamTextSaveTimeoutsRef.current[teamId] = setTimeout(() => {
            persistTeamSharedText(teamId, String(value ?? ''));
            delete teamTextSaveTimeoutsRef.current[teamId];
        }, TEAM_TEXT_SYNC_DELAY_MS);
    }, [persistTeamSharedText]);

    const handleTeamTextBlur = useCallback((team) => {
        if (!team?.id) return;
        if (teamTextSaveTimeoutsRef.current[team.id]) {
            clearTimeout(teamTextSaveTimeoutsRef.current[team.id]);
            delete teamTextSaveTimeoutsRef.current[team.id];
        }
        const sharedText = String(teamTextDraftByTeamId?.[team.id] ?? '');
        persistTeamSharedText(team.id, sharedText);
    }, [persistTeamSharedText, teamTextDraftByTeamId]);

    useEffect(() => () => {
        Object.values(teamTextSaveTimeoutsRef.current).forEach((timeoutId) => {
            clearTimeout(timeoutId);
        });
    }, []);

    // ==============================
    // 12) RENDER
    // ==============================
    return (
        <div className="home-view">
            <h2>Moderator HomeView</h2>
            {attachDebugEnabled && (
                <div className="debug-panel">
                    <span>Socket: {socketDebug.connected ? 'connected' : 'disconnected'}</span>
                    <span>currentQuestionId: {currentQuestion?.id ?? '–'}</span>
                    <span>selectedAttachmentKey: {selectedAttachmentKey || '–'}</span>
                    <span>lastAttachmentCommand: {socketDebug.lastAttachmentCommand || '–'}</span>
                </div>
            )}
            {questionsError && (
                <div className="error-banner" style={{ background: '#fdecea', color: '#b71c1c', padding: '8px', borderRadius: '4px' }}>
                    <span>{questionsError}</span>
                    <Button size="sm" variant="ghost" onClick={fetchQuestions} style={{ marginLeft: '8px' }}>
                        Erneut versuchen
                    </Button>
                </div>
            )}

            {/* QUIZ-KONTROLLEN */}
            <div className="quiz-controls">
                {!quizActive && !startingQuiz && (
                    <Button variant="primary" onClick={handleStartQuiz}>Quiz starten</Button>
                )}
                {startingQuiz && (
                    <div className="quiz-starting">
                        <p>Quiz wird gestartet...</p>
                    </div>
                )}
                {quizActive && (
                    <>
                        <Button onClick={handleStopOrPauseQuiz}>
                            {paused ? 'Quiz fortsetzen' : 'Quiz stoppen/pausieren'}
                        </Button>
                        <Button onClick={handleNextQuestion}>Nächste Frage</Button>
                        <Button onClick={toggleMatrixDisplay}>
                            {showMatrix ? 'Matrix ausblenden' : 'Matrix anzeigen'}
                        </Button>
                        <Button onClick={handleStartCountdown}>Countdown starten</Button>
                        <Button onClick={handleStartPredictionCountdown}>Vorhersage starten</Button>
                        <Button onClick={handleRevealPredictions}>Vorhersagen aufdecken</Button>
                        {countdown !== null && !paused && (
                            <Button onClick={handlePauseCountdown}>Countdown pausieren</Button>
                        )}
                        {countdown !== null && paused && (
                            <Button onClick={handleResumeCountdown}>Countdown fortsetzen</Button>
                        )}
                        <Button onClick={handleToggleModeratorView}>
                            Toggle Moderatorenansicht
                        </Button>
                    </>
                )}
            </div>

            {/* STOP/PAUSE MODAL */}
            <Modal
                isOpen={isStopPauseModalOpen}
                onRequestClose={handleCancelStopPause}
                style={customStyles}
                className="modal-content"
                overlayClassName="modal-overlay"
                contentLabel="Quiz stoppen oder pausieren"
            >
                <h2>Quiz stoppen oder pausieren?</h2>
                <div className="modal-buttons">
                    <Button variant="danger" onClick={() => handleConfirmStop('stop')}>Stoppen</Button>
                    <Button variant="secondary" onClick={() => handleConfirmStop('pause')}>Pausieren</Button>
                    <Button variant="ghost" onClick={handleCancelStopPause}>Abbrechen</Button>
                </div>
            </Modal>

            {/* END ACTION MODAL */}
            <Modal
                isOpen={isEndActionModalOpen}
                onRequestClose={handleCancelEndAction}
                style={customStyles}
                className="modal-content"
                overlayClassName="modal-overlay"
                contentLabel="Quiz beenden Aktion"
            >
                <h2>Quiz beenden</h2>
                <p>Wähle eine Aktion:</p>
                <div className="modal-buttons">
                    <Button variant="danger" onClick={() => handleEndAction('delete')}>
                        Gespielte Fragen löschen
                    </Button>
                    <Button variant="secondary" onClick={() => handleEndAction('reset')}>
                        "gespielt" Status zurücksetzen
                    </Button>
                    <Button variant="ghost" onClick={handleCancelEndAction}>Abbrechen</Button>
                </div>
            </Modal>
            <Modal
                isOpen={jokerReusePrompt}
                onRequestClose={() => {
                    setJokerReusePrompt(false);
                    setJokerReuseTeamId(null);
                }}
                style={customStyles}
                className="modal-content"
                overlayClassName="modal-overlay"
                contentLabel="Joker erneut geben"
            >
                <h2>Joker erneut geben?</h2>
                <p>
                    Der Joker wurde bereits verwendet. Möchtest du ihn wirklich erneut geben?
                </p>
                <div className="modal-buttons">
                    <Button
                        variant="secondary"
                        onClick={() => {
                            setJokerReusePrompt(false);
                            setJokerReuseTeamId(null);
                        }}
                    >
                        Abbrechen
                    </Button>
                    <Button variant="primary" onClick={handleConfirmJokerReuse}>Ja, erneut geben</Button>
                </div>
            </Modal>
            {/* 4) TEAMS / SPIELER */}
            <h3>Teams</h3>
            <ul className="teams-list">
                {teams.map((team) => {
                    const besserwisserAnswer = besserwisserState?.answersByTeamId?.[team.id];
                    const besserwisserCorrect = besserwisserAnswer?.isCorrect;
                    const besserwisserTime = besserwisserAnswer?.deltaMs;
                    const optionLabel = besserwisserAnswer?.option || '—';
                    const timeLabel = Number.isFinite(besserwisserTime)
                        ? formatDeltaMs(besserwisserTime)
                        : '—';
                    const hasBesserwisserAnswer = besserwisserAnswer && (besserwisserCorrect === true || besserwisserCorrect === false);
                    const isActiveTeam = activeTeamId === team.id;
                    const jokerAlreadyUsedByTeam = besserwisserState?.jokerUsedByTeamId?.[team.id] != null;
                    const isBestBesserwisser =
                        bestBesserwisserTeamId != null &&
                        String(bestBesserwisserTeamId) === String(team.id);
                    const isModeratorTeam = team.name?.toLowerCase() === 'moderator';

                    return (
                        <li key={team.id} className={isModeratorTeam ? 'moderator-team' : ''}>
                            <div className="team-header-homeview">
                                <div className="team-info">
                                    <span
                                        className={`team-name ${
                                            isActiveTeam ? 'active-team' : ''
                                        }`}
                                    >
                                        <div>{team.name}</div>{team.punkte}
                                        {renderPredictionForTeam(team.id)}
                                    </span>
                                    <div className="besserwisser-panel">
                                        <span className="besserwisser-label">Besserwisser</span>
                                        <span
                                            className={[
                                                'besserwisser-result',
                                                hasBesserwisserAnswer && 'has-answer',
                                                isBestBesserwisser && 'best',
                                            ]
                                                .filter(Boolean)
                                                .join(' ')}
                                        >
                                            {hasBesserwisserAnswer ? (
                                                <>
                                                    <span className="besserwisser-option">{optionLabel}</span>
                                                    <span
                                                        className={`besserwisser-correct ${besserwisserCorrect ? 'correct' : 'wrong'}`}
                                                    >
                                                        {besserwisserCorrect ? '✓' : '✕'}
                                                    </span>
                                                    <span className="besserwisser-time">{timeLabel}</span>
                                                </>
                                            ) : (
                                                <span className="besserwisser-empty">—</span>
                                            )}
                                        </span>
                                    </div>
                                </div>
                                <div className="points-controls">
                                    <Button size="sm" onClick={() => updatePoints(team.id, team.punkte + 1)}>
                                        +1
                                    </Button>
                                    <Button
                                        size="sm"
                                        onClick={() => updatePoints(team.id, team.punkte - 1)}
                                    >
                                        -1
                                    </Button>
                                    {isModerator && (
                                        <Button size="sm" onClick={() => handleSetActiveTeam(team.id)}>
                                            {isActiveTeam
                                                ? 'Aktives Team entfernen'
                                                : 'Aktives Team setzen'}
                                        </Button>
                                    )}
                                    <Button
                                        size="sm"
                                        variant={jokerAlreadyUsedByTeam ? 'surface' : 'primary'}
                                        className={jokerAlreadyUsedByTeam ? 'disabled-look btn-disabled-look' : ''}
                                        aria-disabled={!isActiveTeam}
                                        onClick={() => {
                                            handleUseBesserwisserJoker(team.id);
                                        }}
                                        title={!isActiveTeam ? 'Nur aktives Team' : undefined}
                                    >
                                        Joker
                                    </Button>
                                </div>
                            </div>

                            {Array.isArray(team.spieler) && team.spieler.length > 0 && (
                                <ul className="players">
                                    {team.spieler.map((player) => (
                                        <li key={player.id || `player-${player.name}-${player.link}`}>
                                            <span className="player-nameM">{player.name || 'Unbekannter Spieler'}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}

                            <div style={{ marginTop: '0.5rem' }}>
                                <textarea
                                    value={String(teamTextDraftByTeamId?.[team.id] ?? teamTextByTeamId?.[team.id] ?? team?.sharedText ?? '')}
                                    onChange={(event) => handleTeamTextChange(team.id, event.target.value)}
                                    onBlur={() => handleTeamTextBlur(team)}
                                    className="team-shared-notes-input"
                                    style={{ width: '100%', minHeight: '72px', resize: 'vertical', overflowY: 'auto' }}
                                    placeholder="Team-Text ..."
                                    readOnly={!isModerator}
                                />
                            </div>
                        </li>
                    );
                })}
            </ul>

            {/* SPIELER OHNE TEAM */}
            {noTeamPlayers.length > 0 && (
                <div className="no-team-players-row">
                    <h3>Ohne Team</h3>
                    <div className="no-team-iframe-list">
                        {noTeamPlayers.map((player) => {
                            const hasValidLink = isValidHttpUrl(player.link);
                            return (
                            <div
                                className="iframe-wrapper small"
                                key={player.id}
                                style={{ position: 'relative' }}
                            >
                                {hasValidLink ? (
                                    <iframe
                                        src={player.link.trim()}
                                        title={`${player.name || 'Unbekannter Spieler'}-liveview`}
                                        scrolling="no"
                                        allowFullScreen
                                        onError={() => handleIframeError(player.id)}
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            border: 'none',
                                        }}
                                    />
                                ) : (
                                    <div className="fallback-content name-only">
                                        {player.name}
                                    </div>
                                )}
                            </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* 2) FRAGE & ANTWORTEN */}
            {quizActive && currentQuestion && (
                <div className="question-and-answers-section card">
                    <div className="card-header">
                        <div>
                            <h3 className="card-title">Aktuelle Frage</h3>
                            <p
                                onClick={handleQuestionClick}
                                className="clickable-question"
                            >
                                {currentQuestion.frage}
                            </p>
                        </div>
                        <div className="chip-row">
                            <span className="chip muted">{(currentQuestion.attachments || []).length} Attachments</span>
                            <span className="chip muted">{currentQuestion.note ? 'Notiz' : 'Keine Notiz'}</span>
                            <span className="chip muted">
                                {(currentQuestion.playsets || []).length > 0
                                    ? (currentQuestion.playsets || []).map((ps) => ps.name).join(', ')
                                    : 'Keine Playsets'}
                            </span>
                            <span className="chip muted">
                                {currentQuestion.lastPlayedAt ? new Date(currentQuestion.lastPlayedAt).toLocaleString() : 'Nie'}
                            </span>
                        </div>
                    </div>

                    <div className="question-layout-grid">
                        <div className="answers-section qa-column">
                            <h3>Antworten:</h3>
                            <div style={{ marginBottom: '10px' }}>
                                <Button
                                    onClick={handleHighlightCorrectAnswerClick}
                                    onMouseEnter={handleMouseEnterCorrect}
                                    onMouseLeave={handleMouseLeaveCorrect}
                                >
                                    Richtige Antwort
                                </Button>
                                <Button
                                    variant="secondary"
                                    onClick={() => setShowCorrectOutline((prev) => !prev)}
                                    style={{ marginLeft: '10px' }}
                                >
                                    Antwort anzeigen
                                </Button>
                            </div>

                            <ul>
                                {currentAnswers.map((ans, index) => {
                                    const isSelected =
                                        activeTeamId && selectedAnswers[activeTeamId] === index;
                                    const isPermanentlyCorrect = correctAnswerIndex === index;

                                    let showAsCorrect = false;
                                    if (
                                        hoveringCorrect &&
                                        currentQuestion?.richtigeAntwort === index
                                    ) {
                                        showAsCorrect = true;
                                    }

                                    const assignedClasses = ['quiz-answer'];
                                    if (showCorrectOutline && currentQuestion?.richtigeAntwort === index) {
                                        assignedClasses.push('correct-outline');
                                    }
                                    if (correctAnswerIndex !== null) {
                                        if (isPermanentlyCorrect) {
                                            assignedClasses.push('correct');
                                        }
                                        if (isSelected && !isPermanentlyCorrect) {
                                            assignedClasses.push('wrong');
                                        }
                                    } else {
                                        assignedClasses.push(isSelected ? 'selected' : 'clickable');
                                        if (showAsCorrect) {
                                            assignedClasses.push('correct-hover');
                                        }
                                    }

                                    return (
                                        <li
                                            key={index}
                                            onClick={() => handleAnswerClick(ans)}
                                            className={assignedClasses.join(' ')}
                                        >
                                            {ans}
                                        </li>
                                    );
                                })}
                            </ul>
                            {feedback && <p className="feedback">{feedback}</p>}
                        </div>

                        <div className="note-column">
                            <NoteEditor
                                value={currentQuestion.note || ''}
                                onChange={handleNoteChange}
                                onSave={handleNoteSave}
                                label="Notizen (nur Moderator)"
                            />
                        </div>
                    </div>
                    <div className="attachment-row-full">
                        <AttachmentRegiePanel
                            questionId={currentQuestion.id}
                            attachments={currentAttachments}
                            getAttachmentState={getAttachmentState}
                            attachmentActions={attachmentActions}
                            onAttachmentSelect={handleAttachmentSelectKey}
                        />
                    </div>
                </div>
            )}

            {/* 5) FRAGEN-AUSWAHL */}
            {quizActive && (
                <div className="question-selection">
                    <h3 className="color-adjust">Nächste Frage auswählen</h3>
                    <div className="filters">
                        <label>
                            Kategorie:
                            <select
                                name="kategorie"
                                value={filter.kategorie}
                                onChange={handleFilterChange}
                            >
                                <option value="">Alle</option>
                                {categories.map((category) => (
                                    <option key={category} value={category}>
                                        {category}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label>
                            Schwierigkeitsgrad:
                            <select
                                name="schwierigkeit"
                                value={filter.schwierigkeit}
                                onChange={handleFilterChange}
                            >
                                <option value="">Alle</option>
                                {difficulties.map((level) => {
                                    const label = getSchwierigkeitsgradString(level);
                                    return (
                                        <option key={level} value={label}>
                                            {label}
                                        </option>
                                    );
                                })}
                            </select>
                        </label>
                        <label>
                            Playset:
                            <select
                                name="playsetId"
                                value={filter.playsetId}
                                onChange={handleFilterChange}
                            >
                                <option value="">Alle</option>
                                {playsetOptions.map((playset) => (
                                    <option key={playset.id} value={playset.id}>
                                        {playset.name}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>
                    <Button
                        onClick={handleRandomQuestion}
                        disabled={filteredQuestions.filter((q) => !q.gespielt).length === 0}
                        size="sm"
                        variant="secondary"
                    >
                        Zufällige Frage auswählen
                    </Button>
                    <div className="questions-list">
                        <h4 className="color-adjust">Verfügbare Fragen:</h4>
                        {filteredQuestions.length === 0 ? (
                            <p>Keine Fragen gefunden.</p>
                        ) : (
                            <div className="question-button-grid">
                                {filteredQuestions.map((q) => {
                                    const selected = selectedQuestionId === q.id;
                                    return (
                                        <Button
                                            key={q.id}
                                            className={`question-select ${selected ? 'selected' : ''} ${q.gespielt ? 'played' : ''}`}
                                            variant={selected ? 'primary' : 'surface'}
                                            onClick={() => handleSelectQuestion(q.id)}
                                            aria-pressed={selected}
                                        >
                                            <div className="question-select-text">{q.frage}</div>
                                            <div className="question-select-meta">
                                                {q.kategorie && <span className="chip">{q.kategorie}</span>}
                                                {q.schwierigkeit && <span className="chip muted">{q.schwierigkeit}</span>}
                                                {(q.playsets || []).length > 0 && (
                                                    <span className="chip">
                                                        {(q.playsets || []).map((ps) => ps.name).filter(Boolean).join(', ')}
                                                    </span>
                                                )}
                                            </div>
                                        </Button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default HomeView;
